import React, { useMemo, useState } from 'react';
import type { AccountChange360 } from '../lib/userDetailService';

const tableLabels: Record<string, string> = {
  employer_profiles: 'Profil entreprise',
  employer_establishments: 'Établissement',
  job_offers: 'Offre',
  profiles: 'Compte',
  candidate_profiles: 'Profil candidat',
  candidate_experiences: 'Expérience',
  candidate_languages: 'Langue',
  candidate_diplomas: 'Diplôme',
};

const sourceLabels: Record<string, string> = {
  client: 'Application',
  admin: 'Backoffice',
  service_role: 'Backend (service)',
  backend: 'Backend SQL',
  unknown: 'Inconnu',
};

const originLabels: Record<string, string> = {
  save_employer_document: 'RPC document recruteur',
  save_candidate_document: 'RPC document candidat',
  save_job_offer_document: 'RPC document offre',
};

const opLabels: Record<string, string> = {
  insert: 'Création',
  update: 'Modification',
  delete: 'Suppression',
};

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return v === '' ? '∅' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 120 ? `${s.slice(0, 117)}…` : s;
  } catch {
    return String(v);
  }
}

const AccountChangeHistory: React.FC<{ entries: AccountChange360[] }> = ({ entries }) => {
  const [wipesOnly, setWipesOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (wipesOnly ? entries.filter((e) => e.hasWipe) : entries),
    [entries, wipesOnly],
  );
  const wipeCount = entries.filter((e) => e.hasWipe).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-800">
          Historique des modifications ({filtered.length}{wipesOnly ? ` / ${entries.length}` : ''})
        </h3>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={wipesOnly}
            onChange={(ev) => setWipesOnly(ev.target.checked)}
            className="rounded border-gray-300"
          />
          Effacements seulement{wipeCount ? ` (${wipeCount})` : ''}
        </label>
      </div>
      <div className="p-5">
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-400">
            {entries.length === 0
              ? 'Aucun événement depuis la mise en place du journal. Les prochaines écritures (app, admin ou backend) apparaîtront ici.'
              : 'Aucun effacement dans cet historique.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((e) => {
              const open = openId === e.id;
              const blockedKeys = Object.keys(e.blockedClears || {});
              const changeKeys = Object.keys(e.changes || {}).filter((k) => k !== 'after' && k !== 'before');
              return (
                <li key={e.id} className={`border rounded-lg ${e.hasWipe ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100'}`}>
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : e.id)}
                    className="w-full text-left px-3 py-2 flex items-start gap-3"
                  >
                    <span className="text-gray-400 text-xs w-4 mt-0.5">{open ? '▼' : '▶'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-gray-500">{e.occurredAt}</span>
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-700">
                          {sourceLabels[e.source] || e.source}
                        </span>
                        <span className="text-xs font-medium text-gray-800">
                          {opLabels[e.op] || e.op} · {tableLabels[e.tableName] || e.tableName}
                        </span>
                        {e.clearedFields.length > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-50 text-red-700">
                            Effacé : {e.clearedFields.join(', ')}
                          </span>
                        )}
                        {blockedKeys.length > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800">
                            Wipe bloqué : {blockedKeys.join(', ')}
                          </span>
                        )}
                      </div>
                      {e.origin && (
                        <p className="text-[11px] text-gray-400 mt-0.5">{originLabels[e.origin] || e.origin}</p>
                      )}
                    </div>
                  </button>
                  {open && (
                    <div className="px-4 pb-3 pt-0 text-xs text-gray-700 space-y-2 border-t border-gray-100">
                      {changeKeys.length > 0 && (
                        <div>
                          <p className="uppercase tracking-wide text-[10px] text-gray-400 font-semibold mt-2 mb-1">Champs</p>
                          <ul className="space-y-0.5">
                            {changeKeys.map((k) => (
                              <li key={k} className="font-mono break-all">
                                <span className="text-gray-500">{k}</span>
                                {' : '}
                                <span className="text-red-700">{fmtVal(e.changes[k]?.from)}</span>
                                {' → '}
                                <span className="text-emerald-700">{fmtVal(e.changes[k]?.to)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {blockedKeys.length > 0 && (
                        <div>
                          <p className="uppercase tracking-wide text-[10px] text-amber-700 font-semibold mb-1">
                            Tentatives d’effacement refusées (payload vide, valeur conservée)
                          </p>
                          <ul className="space-y-0.5">
                            {blockedKeys.map((k) => (
                              <li key={k} className="font-mono break-all">
                                <span className="text-gray-500">{k}</span>
                                {' : '}
                                <span>{fmtVal(e.blockedClears[k]?.from)}</span>
                                {' ↛ '}
                                <span className="text-amber-800">{fmtVal(e.blockedClears[k]?.payload)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {e.op === 'insert' && e.changes.after != null && (
                        <pre className="bg-gray-50 rounded p-2 overflow-auto max-h-40 text-[11px]">{fmtVal(e.changes.after)}</pre>
                      )}
                      {e.op === 'delete' && e.changes.before != null && (
                        <pre className="bg-gray-50 rounded p-2 overflow-auto max-h-40 text-[11px]">{fmtVal(e.changes.before)}</pre>
                      )}
                      {e.actorId && <p className="text-gray-400 font-mono">acteur {e.actorId}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AccountChangeHistory;
